import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import SearchableSelect from './SearchableSelect';
import useAppStore from '../store';

interface DumpsterMoveToDepotFormProps {
  dumpsterId: string;
  onSuccess: () => void;
  onCancel: () => void;
  loadInitialData?: () => Promise<void>;
}

const DumpsterMoveToDepotForm: React.FC<DumpsterMoveToDepotFormProps> = ({
  dumpsterId,
  onSuccess,
  onCancel,
  loadInitialData
}) => {
  const { depots } = useAppStore();
  const [depotId, setDepotId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    
    if (!depotId) {
      setError('Veuillez sélectionner un dépôt');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const depot = depots.find(d => d.id === depotId);
      if (!depot) {
        throw new Error('Dépôt non trouvé');
      }

      const { error: updateError } = await supabase
        .from('dumpsters')
        .update({
          depot_id: depotId,
          client_id: null,
          position_lat: depot.position.lat,
          position_lng: depot.position.lng,
          drop_date: new Date().toISOString(),
          driver_id: null
        })
        .eq('id', dumpsterId);

      if (updateError) throw updateError;

      // Only call loadInitialData if it exists
      if (loadInitialData) {
        await loadInitialData();
      }

      onSuccess();
    } catch (err) {
      console.error('Error moving dumpster:', err);
      setError('Une erreur est survenue lors du déplacement de la benne');
      setIsSubmitting(false); // Allow retrying on error
    }
  };

  const depotOptions = depots.map(depot => ({
    value: depot.id,
    label: depot.name
  }));

  return (
    <div className="mt-3 space-y-3" onClick={e => e.stopPropagation()}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <SearchableSelect
            options={depotOptions}
            value={depotId}
            onChange={(value) => {
              setDepotId(value);
              setError(null);
            }}
            placeholder="Sélectionner un dépôt"
            searchPlaceholder="Rechercher un dépôt..."
          />
          {error && (
            <p className="mt-1 text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="flex space-x-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            disabled={isSubmitting}
          >
            Annuler
          </button>
          
          <button
            type="submit"
            className="flex-1 px-3 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!depotId || isSubmitting}
          >
            {isSubmitting ? 'Déplacement...' : 'Déplacer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DumpsterMoveToDepotForm;